import { useQuery } from "@apollo/client";
import { useLocale, useTranslations } from "next-intl";
import normalizeProductFilterQuery from "../utils/normalizeProductFilterQuery";
import { AssortmentProductsQuery } from "../queries/assortmentQueries";

export enum ProductsOrderBy {
  default = "default",
  meta_migelConformitySortOrder_ASC = "meta_migelConformitySortOrder_ASC",
  meta_inStock_DESC = "meta_inStock_DESC",
  meta_productTitleDe_ASC = "meta_productTitleDe_ASC",
  meta_productTitleDe_DESC = "meta_productTitleDe_DESC",
  meta_productTitleFr_ASC = "meta_productTitleFr_ASC",
  meta_productTitleFr_DESC = "meta_productTitleFr_DESC",
  meta_productTitleIt_ASC = "meta_productTitleIt_ASC",
  meta_productTitleIt_DESC = "meta_productTitleIt_DESC",
  warehousing_sku_DESC = "warehousing_sku_DESC",
  warehousing_sku_ASC = "warehousing_sku_ASC",
  meta_unitPrice_DESC = "meta_unitPrice_DESC",
  meta_unitPrice_ASC = "meta_unitPrice_ASC",
}

export type AssortmentProductsQueryResult = {
  assortment: {
    _id: string;
    searchProducts: {
      productsCount: any;
      filteredProductsCount: any;
      filters: Array<{
        isSelected: boolean;
        definition: {
          key: string;
          texts?: {
            title: string;
          };
          value: string;
        };
        options: Array<{
          isSelected: boolean;
          definition: {
            value: string;
            texts?: {
              title: string;
            };
          };
          filteredProductsCount: number;
        }>;
      }>;
      products: Array<{
        _id: string;
        sku: string;
        simulatedPrice: {
          amount: number;
          currency: string;
        };
        assortmentPaths: {
          links: Array<{
            assortmentId: string;
          }>;
        };
      }>;
    };
  };
};

const useAssortmentProducts = ({
  assortmentId,
  formState,
  offset = 0,
}: {
  assortmentId: string;
  offset?: number;
  formState: Record<string, any>;
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const { sort, search, ...filterState } = formState;
  const [orderBy] = sort || ["meta_inStock_DESC"];
  const queryString = search?.[0];
  const filterQuery = normalizeProductFilterQuery(filterState);

  const {
    data: { assortment } = {},
    previousData: { assortment: previousAssortment } = {},
    ...rest
  } = useQuery<AssortmentProductsQueryResult>(AssortmentProductsQuery, {
    notifyOnNetworkStatusChange: true,
    returnPartialData: true,
    variables: {
      offset,
      assortmentId,
      queryString,
      filterQuery,
      orderBy,
    },
    skip: !assortmentId,
  });

  const capitalizedLang = locale[0].toUpperCase() + locale[1];
  const SearchSortOptions = {
    [`meta_inStock_DESC`]: t("order_by_stock_descending"),
    [`meta_unitPrice_ASC`]: t("order_by_price_ascending"),
    [`meta_unitPrice_DESC`]: t("order_by_price_descending"),
    warehousing_sku_ASC: t("order_by_sku_ascending"),
    warehousing_sku_DESC: t("order_by_sku_descending"),
    [`meta_productTitle${capitalizedLang}_ASC`]: t("order_by_title_ascending"),
    [`meta_productTitle${capitalizedLang}_DESC`]: t(
      "order_by_title_descending",
    ),
  };
  const sortOptions = Object.entries(SearchSortOptions).map(
    ([value, label]) => ({
      label,
      value,
    }),
  );

  return {
    sortOptions,
    queryString,
    ...(assortment?.searchProducts || previousAssortment?.searchProducts || {}),
    ...rest,
  };
};

export default useAssortmentProducts;
